import React from 'react';
import { kebabCase } from 'lodash';
import { PatientWithAccessRightDTO } from '@peartherapeutics/weaver-api-client';
import AuthorizationComponent from 'components/common/AuthorizationComponent';
import { programs, PROGRAM_CONFIG } from 'utils/constants';
import {
  formatDate,
  formatDateWithTimezone,
  DATE_FORMATS,
  displayDateTimeFromNow,
} from 'utils/dateTime';
import { isSupportedProgram } from 'utils/routeHelper';
import { scopes } from 'utils/scope';

import './styles.scss';
import ReRxButton from '../ReRxDialog';
import TableActions from '../TableActions';

export interface IColumnProps {
  styleName: string;
  title?: string;
  component?: Nullable<JSX.Element>;
  text?: Nullable<string | number | JSX.Element>;
}

export interface IRowProps {
  patient: PatientWithAccessRightDTO;
  updatePatientData: (patientData: PatientWithAccessRightDTO) => void;
  handleOnClick: () => void;
  actions: {
    hasPatientReadPermission: boolean;
  };
}

const NULL_DISPLAY = '-';

export const Column = ({ styleName, title, component, text }: IColumnProps) => (
  <td role={styleName} styleName={styleName} title={title}>
    {component || <div>{text}</div>}
  </td>
);

const generateTableActionsComponent = (patient: PatientWithAccessRightDTO) => {
  if (
    patient.currentAccessRight !== undefined &&
    [
      PROGRAM_CONFIG[programs.RESET].name,
      PROGRAM_CONFIG[programs.RESETO].name,
    ].includes(patient.currentAccessRight.program)
  ) {
    if (
      formatDateWithTimezone(
        patient.currentAccessRight.startDate,
        patient.currentAccessRight.timeZone,
        DATE_FORMATS.M_D_YYYY,
      )
    ) {
      return <TableActions patient={patient} />;
    }
    return <div styleName="no-data">No data</div>;
  }
  return <div styleName="not-applicable">Not applicable</div>;
};

export const ProgressBar = ({ percentage }: { percentage: string }) => (
  <div styleName="progress-bar" style={{ width: percentage }} />
);

export const generateDaysRemainingComponent = (
  patient: PatientWithAccessRightDTO,
  updatePatientData: (patientData: PatientWithAccessRightDTO) => void,
): { title: string; component?: React.ReactElement } | undefined => {
  const hasCurrentAccessRight = patient.currentAccessRight !== undefined;
  if (hasCurrentAccessRight) {
    const { status, durationInDays, daysRemaining } =
      patient.currentAccessRight!;
    if (
      status !== 'active' &&
      status !== 'expired' &&
      status !== 'unredeemed' &&
      status !== 'started'
    ) {
      return { title: 'Rx Days Remaining' };
    }
    const percentage = daysRemaining
      ? `calc(${(durationInDays - daysRemaining) / durationInDays} * 98%)`
      : '100%';

    return {
      title: `Rx Days Remaining: ${daysRemaining ?? 0} of ${durationInDays}`,
      component: (
        <div styleName="day-remaining-column">
          {daysRemaining !== null && (
            <div styleName="progress-day-remaining">
              <ProgressBar percentage={percentage} />
            </div>
          )}
          <span data-testid="day-remaining-number">{daysRemaining}</span>
          {daysRemaining !== null &&
            daysRemaining !== undefined &&
            daysRemaining <= 9 && (
              <AuthorizationComponent
                requiredScope={scopes.PRESCRIPTION_REPRESCRIBE}
              >
                <div styleName="represcription-button">
                  <ReRxButton
                    patient={patient}
                    updatePatientData={updatePatientData}
                  />
                </div>
              </AuthorizationComponent>
            )}
        </div>
      ),
    };
  }
  return undefined;
};

const generateColumns = (
  patient: PatientWithAccessRightDTO,
  updatePatientData: (patientData: PatientWithAccessRightDTO) => void,
  actions: {
    hasPatientReadPermission: boolean;
  },
): IColumnProps[] => {
  // TODO [WB-2859] Move this logic into the DTO and into Weaver API.
  const patientHCP = patient.healthCareProvider;
  const hcpName = patientHCP
    ? `${patientHCP?.firstName || ''} ${patientHCP?.lastName || ''}`
    : NULL_DISPLAY;
  let daysRemainingComponent = generateDaysRemainingComponent(
    patient,
    updatePatientData,
  );
  if (daysRemainingComponent === undefined) {
    daysRemainingComponent = {
      title: 'Rx Days Remaining',
      component: <div styleName="day-remaining-column">{NULL_DISPLAY}</div>,
    };
  }
  const columns: IColumnProps[] = [
    {
      styleName: 'name-column',
      title: 'Last Name',
      text: patient.lastName,
    },
    {
      styleName: 'name-column',
      title: 'First Name',
      text: patient.firstName,
    },
    {
      styleName: 'name-column',
      title: 'External ID',
      text: patient.externalRef,
    },
    {
      styleName: 'dob-column',
      title: 'DOB',
      text: patient.birthDate
        ? formatDate(patient.birthDate, DATE_FORMATS.M_D_YYYY)
        : NULL_DISPLAY,
    },
    {
      styleName: 'program-column',
      title: 'Program',
      text: patient.currentAccessRight?.program ?? NULL_DISPLAY,
    },
    {
      styleName: 'primary-clinician-column',
      title: 'Health Care Provider',
      text: hcpName,
    },
    {
      styleName: 'last-opened-column',
      title: 'Last Opened',
      text: patient.lastOpened
        ? displayDateTimeFromNow(patient.lastOpened)
        : NULL_DISPLAY,
    },
    {
      styleName: 'rx-date-column',
      title: 'Rx Start Date',
      text: patient.currentAccessRight?.startDate
        ? formatDateWithTimezone(
            patient.currentAccessRight.startDate,
            patient.currentAccessRight.timeZone,
            DATE_FORMATS.M_D_YYYY,
          )
        : NULL_DISPLAY,
    },
    {
      styleName: 'rx-date-remaining-column',
      ...daysRemainingComponent,
    },
  ];

  if (actions.hasPatientReadPermission) {
    columns.push({
      styleName: 'last-visit-column',
      component: generateTableActionsComponent(patient),
    });
  }
  return columns;
};

const Row = ({
  patient,
  updatePatientData,
  handleOnClick,
  actions,
}: IRowProps) => {
  const isClickable =
    patient.currentAccessRight &&
    isSupportedProgram(patient.currentAccessRight.program.toLowerCase()) &&
    actions.hasPatientReadPermission;

  return (
    <tr
      data-testid="patient-summary-table-rows"
      styleName={isClickable ? 'data-row clickable' : 'data-row'}
      onClick={handleOnClick}
    >
      {generateColumns(patient, updatePatientData, actions).map((col) => (
        <Column
          key={`${patient.id}-${kebabCase(col.title || 'Patient Snapshot')}`}
          {...col}
        />
      ))}
    </tr>
  );
};

export default Row;
